import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DashboardLayout,
  PrimaryButton,
  SecondaryButton,
} from '../../../components';
import {useParams} from 'react-router-dom';
import * as MainAssets from '../../../assets/images/images';
import ConfigPanel from './ConfigPanel';
import {EasyOnboardContext} from '../../../helpers/context';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {
  fetchAuthWidget,
  updateAuthWidget,
} from '../../../requests/queries/easyAuth';
import DeleteFlowModal from '../../EasyOnboard/ConfirmFlowDelete';
import {useDispatch, useSelector} from 'react-redux';
import {UPDATE_WIDGET} from '../../../store/types';
import {toast} from 'react-toastify';
import ButtonLoader from '../../../components/ButtonLoader';
import * as Actions from '../../../store/actions';
import inverseColor from '../../../helpers/colorInversion';
import {PlayIc} from '../../../components/images';
import Breadcrumbs from 'components/BreadCrumbs';
import {PAGES_URL} from 'helpers/constants';

function UpdateAuthFlow() {
  const {slug} = useParams();
  const [{deleteModal}, setDeleteModal] = useState({deleteModal: false});
  const [type, setType] = useState('draft');
  const [nameError, setNameError] = useState(false);
  const textAreaRef = useRef(null);
  const {userDetails} = useSelector(state => state?.auth);
  const {singleWidget, singleApp} = useSelector(state => state?.widgets);
  const {easyAuthWidgetData, setEasyAuthWidgetData} =
    useContext(EasyOnboardContext);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const handleFocus = () => {
    textAreaRef.current.focus();
  };
  const hasWidgetUrl = !!localStorage.subdomain && !!userDetails?.widgetUrl;
  const customWidgetUrl = hasWidgetUrl
    ? userDetails?.widgetUrl
    : userDetails?.company?.widget_url;

  const {
    data: authWidget,
    isLoading: authWidgetLoading,
    isFetching: authWidgetFetching,
  } = useQuery(['auth-widget', slug], () => fetchAuthWidget(slug));

  useEffect(() => {
    if (!authWidgetLoading) {
      delete authWidget?.widget?.pages;
      dispatch({payload: authWidget, type: UPDATE_WIDGET});
      dispatch(Actions.setSingleWidget(authWidget));
      setEasyAuthWidgetData({...authWidget?.widget});
    }
  }, [authWidget, authWidgetLoading, dispatch, setEasyAuthWidgetData]);

  const handlePreview = () => {
    window.open(
      `${
        customWidgetUrl ?? process.env.REACT_APP_WIDGET_BASE_URL
      }/register?widget_id=${slug}`,
      '_blank',
    );
  };

  // const {refetch, isFetching: previewLoading} = useQuery(
  //   ['auth-widget-preview', slug],
  //   () => previewAuthWidget(slug),
  //   {
  //     onSuccess: data => {
  //       type === 'preview' &&
  //         window.open(
  //           `${
  //             customWidgetUrl ?? process.env.REACT_APP_WIDGET_BASE_URL
  //           }/authenticate?widget_id=${slug}&token=${data?.previewCode}`,
  //           '_blank',
  //         );
  //     },
  //     enabled: false,
  //   },
  // );

  const {mutate, isLoading} = useMutation(updateAuthWidget);

  const widgetName = easyAuthWidgetData?.name || singleWidget?.widget?.name;

  const publishWidget = useCallback(
    async type => {
      setType(type);
      const newType = ['draft', 'unpublish']?.includes(type) ? '' : type;
      if (!widgetName || widgetName?.includes('Untitled')) {
        setNameError(true);
        return toast.error(
          `${
            widgetName?.includes('Untitled')
              ? 'Name cannot be "Untitled", please update'
              : 'Please name your widget flow'
          }`,
        );
      }
      const message =
        type === 'publish'
          ? 'Auth Widget changes applied successfully'
          : 'Auth Widget draft updated successfully';

      try {
        const {name, country, support_email, redirect_url} = easyAuthWidgetData;

        const data = {
          name,
          country,
          redirect_url,
          support_email,
          published: !!newType,
        };

        await mutate(
          {id: authWidget?.widget?._id, data},
          {
            onSuccess: () => {
              toast.success(message);
              queryClient.refetchQueries();
            },
            onError: error => {
              toast.error(error?.response?.data?.error);
            },
          },
        );
      } catch (e) {
        console.log(e);
      }
    },
    [
      authWidget?.widget?._id,
      easyAuthWidgetData,
      mutate,
      queryClient,
      widgetName,
    ],
  );

  const appLoadingState = useMemo(
    () => authWidgetLoading || isLoading || authWidgetFetching,
    [authWidgetFetching, authWidgetLoading, isLoading],
  );

  return (
    <DashboardLayout
      className="!mb-0"
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">
          Easy Authentication
        </p>
      }
    >
      <div className="flex items-center min-h-[52px] py-2 flex-wrap gap-3 justify-between mx-[-32px] px-8 border border-transparent border-b-[#E4E4E6]">
        <Breadcrumbs
          items={[
            {
              label: 'Easy Authentication',
              path: PAGES_URL.AUTHENTICATION_FLOW,
            },
            {label: 'Update a Flow'},
          ]}
        />
      </div>
      {type !== '' && appLoadingState && (
        <div className="fixed  left-0 top-0 z-50  w-full h-full flex items-center justify-center">
          <ButtonLoader color="#3F7CDB" />
          <span className="text-sm mt-16 text-body font-medium italic">
            ...
            {type === 'publish' ? 'updat' : type === 'draft' ? 'sav' : type}
            ing
          </span>
        </div>
      )}

      <DeleteFlowModal
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
        flowId={slug}
        redirect={true}
        route="/authentication-flow"
      />
      <div
        className="flex gap-[3.72%] bg-[#f9f9f9] basis-full h-full mx-[-32px]"
        style={{
          opacity: type !== '' && appLoadingState && 0.2,
        }}
      >
        <div className="w-full basis-[30.709%] flex flex-col h-screen pl-8">
          <div className="flex items-center gap-1 mt-6">
            <input
              ref={textAreaRef}
              className={`text-base font-medium ${
                !authWidget?.widget?.name ? 'text-grey40 italic' : 'text-grey'
              } outline-none focus:outline focus:outline-offset-2 focus:outline-1 ${
                nameError
                  ? 'outline outline-danger outline-offset-2 outline-2'
                  : ''
              }  `}
              type="text"
              placeholder="Widget flow name here..."
              value={easyAuthWidgetData?.name || singleWidget?.widget?.name}
              style={{
                maxWidth: 350,
                maxHeight: 50,
                border: 'none',
              }}
              onChange={e => {
                setNameError(false);
                setEasyAuthWidgetData({
                  ...easyAuthWidgetData,
                  name: e.target.value,
                });
              }}
            />
            <img
              src={MainAssets.edit}
              onClick={handleFocus}
              alt=""
              className="cursor-pointer ml-2 mt-2"
            />
          </div>
          <div className="mt-6 overflow-y-auto custom-flow">
            <ConfigPanel
              setSelectedApp={() => {}}
              isLoading={authWidgetLoading}
            />
          </div>
        </div>

        <div className="bg-black25 basis-[65.571%] custom-flow overflow-y-auto">
          <div className="bg-white80 relative flex w-full right-0 items-center gap-4 py-2 px-12 justify-end">
            {!authWidgetLoading && !authWidget?.widget?.published && (
              <SecondaryButton
                buttonText="Save as Draft"
                btnHeight="h-7"
                yPadding="pt-[9px] pb-[10px]"
                className="text-xs"
                loading={type === 'draft' && isLoading}
                onClick={() => publishWidget('draft')}
                disabled={singleWidget?.widget?.name?.length === 0}
              />
            )}
            <PrimaryButton
              buttonText={
                authWidget?.widget?.published ? 'Apply Changes' : 'Publish'
              }
              xPadding="px-5"
              yPadding="py-2"
              fontSize="text-xs"
              className="h-7"
              onClick={() => publishWidget('publish')}
              disabled={singleWidget?.widget?.name?.length === 0}
              loading={type === 'publish' && isLoading}
            />
            <button
              onClick={() => {
                setType('preview');
                // refetch();
                handlePreview();
              }}
              disabled={
                // (type === 'preview' && previewLoading) ||
                !authWidget?.widget?.published
              }
              // style={{
              //   opacity: type === 'preview' && previewLoading ? 0.5 : 1,
              //   cursor:
              //     (type === 'preview' && previewLoading) ||
              //     !authWidget?.widget?.published
              //       ? 'not-allowed'
              //       : 'pointer',
              // }}
              className="px-2 pt-[9px] pb-[7px] flex items-end gap-1 text-xs font-medium rounded text-brandBlue"
            >
              <PlayIc />
              {type === 'preview' ? 'Processing...' : ' Preview widget'}
            </button>

            {!authWidget?.widget?.published &&
              !authWidget?.widget?.hasBeenPublished && (
                <button
                  onClick={() => {
                    setType('delete');
                    setDeleteModal({deleteModal: true});
                  }}
                  className="px-2 pt-[9px] pb-[7px] text-xs text-white bg-danger rounded font-medium"
                >
                  {type === 'delete' && isLoading ? 'Deleting...' : 'Delete'}
                </button>
              )}

            {authWidget?.widget?.published &&
              authWidget?.widget?.hasBeenPublished && (
                <button
                  onClick={() => {
                    setType('unpublish');
                    publishWidget('unpublish');
                  }}
                  className="px-2 pt-[9px] pb-[7px] text-xs text-white bg-danger rounded font-medium"
                >
                  {type === 'unpublish' && isLoading
                    ? 'Unpublishing...'
                    : 'Unpublish'}
                </button>
              )}
          </div>

          <div
            className="min-h-screen"
            style={{
              opacity: 1,
              transition: 'opacity 0.3s ease-in-out',
            }}
          >
            <div className="flex flex-col items-center justify-center max-w-lg mx-auto py-4 mt-[50px]">
              <div className="flex flex-col items-center w-full px-6 py-8 bg-white rounded-lg">
                <div className="flex justify-between w-full">
                  <button disabled>
                    <img
                      src={MainAssets.widgetBack}
                      alt=""
                      className="pt-4 pl-4"
                    />
                  </button>
                  <button disabled>
                    <img
                      src={MainAssets.closeWidget}
                      alt=""
                      className="pt-4 pr-4"
                    />
                  </button>
                </div>
                <header className="flex flex-col items-center mt-2">
                  <img
                    src={singleApp?.logo || MainAssets.logo}
                    alt=""
                    className="max-h-10"
                  />
                </header>
                <p className="text-grey font-medium mt-2">Say cheese!</p>
                <div className="mt-2 px-2 py-1 bg-[#BFD3F3] rounded-2xl">
                  <p className="text-[#0D192C] text-xs">This looks good</p>
                </div>

                <img
                  src={MainAssets.SmilingPhoto}
                  alt="smiling"
                  className="w-[246px] h-[246px] rounded-full mt-4"
                />

                <div className="mt-6 px-2 py-1 bg-[#F8EBEA] rounded-2xl flex items-center gap-2">
                  <img src={MainAssets.infoRed} alt="red" />
                  <p className="text-[#B42318] text-xs mt-[2px]">
                    Please make sure your are in a well lit environment
                  </p>
                </div>

                <button
                  disabled
                  className="w-full h-12 max-w-sm mt-6 font-medium  rounded text-tiny bg-brandBlue"
                  style={{
                    backgroundColor:
                      singleApp?.color || singleApp?.color_code || '',
                    color: inverseColor(
                      singleApp?.color || singleApp?.color_code || '#ffffff',
                    ),
                  }}
                >
                  Continue
                </button>

                <div className="flex items-center justify-center gap-2 my-4">
                  <span className="text-sm text-grey">Powered by</span>
                  <img src={MainAssets.logo} alt="" width={30.55} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default UpdateAuthFlow;
